import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  Col,
  Row,
  Statistic,
  Typography,
  Tabs,
  Table,
  Tag,
  Space,
  Button,
  Empty,
} from 'antd';
import {
  AlertOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DisconnectOutlined,
  MobileOutlined,
  ReloadOutlined,
  SafetyOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { complianceApi, type ComplianceItemDto, type ComplianceSummaryDto } from '@/api/complianceApi';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

/**
 * Dashboard de compliance: muestra agregados de la flota y permite drill-down
 * a listas detalladas por tipo de problema.
 */
export function CompliancePage() {
  const [tabKey, setTabKey] = useState<string>('summary');

  const summaryQuery = useQuery({
    queryKey: ['compliance', 'summary'],
    queryFn: () => complianceApi.getSummary({}),
  });

  const offlineQuery = useQuery({
    queryKey: ['compliance', 'offline'],
    queryFn: () => complianceApi.getOffline({}),
    enabled: tabKey === 'offline',
  });

  const noProfileQuery = useQuery({
    queryKey: ['compliance', 'without-profile'],
    queryFn: () => complianceApi.getWithoutProfile({}),
    enabled: tabKey === 'no-profile',
  });

  const pendingQuery = useQuery({
    queryKey: ['compliance', 'pending'],
    queryFn: () => complianceApi.getPendingEnrollment({}),
    enabled: tabKey === 'pending',
  });

  const summary = summaryQuery.data;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>
          <SafetyOutlined style={{ marginRight: 12 }} />
          Reporte de Compliance
        </Title>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => {
            summaryQuery.refetch();
            offlineQuery.refetch();
            noProfileQuery.refetch();
            pendingQuery.refetch();
          }}
          loading={summaryQuery.isFetching}
        >
          Refrescar
        </Button>
      </div>

      {/* Tarjetas de stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total dispositivos"
              value={summary?.totalDevices ?? 0}
              prefix={<MobileOutlined />}
              loading={summaryQuery.isLoading}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {summary?.activeDevices ?? 0} activos
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Offline"
              value={summary?.offlineDevices ?? 0}
              prefix={<DisconnectOutlined />}
              valueStyle={{ color: (summary?.offlineDevices ?? 0) > 0 ? '#cf1322' : undefined }}
              loading={summaryQuery.isLoading}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Sin sincronizar &gt;7 dias
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Pendiente enrolar"
              value={summary?.pendingEnrollment ?? 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: (summary?.pendingEnrollment ?? 0) > 0 ? '#d48806' : undefined }}
              loading={summaryQuery.isLoading}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Sin GoogleDeviceId
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Sin perfil"
              value={summary?.withoutProfile ?? 0}
              prefix={<WarningOutlined />}
              valueStyle={{ color: (summary?.withoutProfile ?? 0) > 0 ? '#d48806' : undefined }}
              loading={summaryQuery.isLoading}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Sin policy aplicada
            </Text>
          </Card>
        </Col>
      </Row>

      {/* Tabs con detalles */}
      <Card>
        <Tabs
          activeKey={tabKey}
          onChange={setTabKey}
          items={[
            {
              key: 'summary',
              label: 'Distribuciones',
              children: <DistributionsTab summary={summary} loading={summaryQuery.isLoading} />,
            },
            {
              key: 'offline',
              label: (
                <>
                  Offline {summary && summary.offlineDevices > 0 && (
                    <Tag color="error" style={{ marginLeft: 8 }}>{summary.offlineDevices}</Tag>
                  )}
                </>
              ),
              children: (
                <DeviceListTab
                  items={offlineQuery.data ?? []}
                  loading={offlineQuery.isLoading}
                  emptyText="No hay devices offline. Todos sincronizaron recientemente."
                />
              ),
            },
            {
              key: 'no-profile',
              label: (
                <>
                  Sin perfil {summary && summary.withoutProfile > 0 && (
                    <Tag color="warning" style={{ marginLeft: 8 }}>{summary.withoutProfile}</Tag>
                  )}
                </>
              ),
              children: (
                <DeviceListTab
                  items={noProfileQuery.data ?? []}
                  loading={noProfileQuery.isLoading}
                  emptyText="Todos los devices tienen perfil asignado."
                />
              ),
            },
            {
              key: 'pending',
              label: (
                <>
                  Pendiente enrolar {summary && summary.pendingEnrollment > 0 && (
                    <Tag color="default" style={{ marginLeft: 8 }}>{summary.pendingEnrollment}</Tag>
                  )}
                </>
              ),
              children: (
                <DeviceListTab
                  items={pendingQuery.data ?? []}
                  loading={pendingQuery.isLoading}
                  emptyText="No hay devices pendientes de enrolar."
                />
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
}

function DistributionsTab({
  summary,
  loading,
}: {
  summary: ComplianceSummaryDto | undefined;
  loading: boolean;
}) {
  if (loading) return <Empty description="Cargando..." />;
  if (!summary) return <Empty />;

  const renderDist = (title: string, dist: Record<string, number>) => {
    const entries = Object.entries(dist).sort((a, b) => b[1] - a[1]);
    if (entries.length === 0) return null;
    return (
      <Col xs={24} md={8}>
        <Card title={title} size="small">
          {entries.map(([key, count]) => (
            <div key={key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <Text>{key || '(sin dato)'}</Text>
              <Tag>{count}</Tag>
            </div>
          ))}
        </Card>
      </Col>
    );
  };

  return (
    <Row gutter={[16, 16]}>
      {renderDist('Por version Android', summary.androidVersionDistribution)}
      {renderDist('Por marca', summary.brandDistribution)}
      {renderDist('Por estado', summary.stateDistribution)}
    </Row>
  );
}

function DeviceListTab({
  items,
  loading,
  emptyText,
}: {
  items: ComplianceItemDto[];
  loading: boolean;
  emptyText: string;
}) {
  if (!loading && items.length === 0) {
    return (
      <Empty
        image={<CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />}
        description={emptyText}
      />
    );
  }

  return (
    <Table<ComplianceItemDto>
      rowKey="deviceId"
      loading={loading}
      dataSource={items}
      pagination={{ pageSize: 20, showSizeChanger: true }}
      columns={[
        {
          title: 'Device',
          key: 'device',
          render: (_, item) => (
            <Space direction="vertical" size={0}>
              <Link to={`/devices/${item.deviceId}`}>
                <Text strong>{item.serialNumber ?? `#${item.deviceId}`}</Text>
              </Link>
              <Text type="secondary" style={{ fontSize: 12 }}>{item.model ?? '-'}</Text>
            </Space>
          ),
        },
        {
          title: 'Asignado a',
          dataIndex: 'assignedToName',
          render: (v) => v ?? <Text type="secondary">-</Text>,
        },
        {
          title: 'Empresa',
          dataIndex: 'enterpriseName',
        },
        {
          title: 'Perfil',
          dataIndex: 'profileName',
          render: (v) => v ?? <Tag color="warning">Sin asignar</Tag>,
        },
        {
          title: 'Ultimo sync',
          dataIndex: 'lastSyncAt',
          render: (v) => v ? dayjs(v).fromNow() : <Text type="secondary">Nunca</Text>,
        },
        {
          title: 'Problema',
          dataIndex: 'issue',
          render: (v, item) => {
            const color = item.severity === 'error' ? 'error' : item.severity === 'warning' ? 'warning' : 'default';
            const icon = item.severity === 'error' ? <AlertOutlined /> : <WarningOutlined />;
            return <Tag color={color} icon={icon}>{v}</Tag>;
          },
        },
      ]}
    />
  );
}
